import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from '@tmapy/redux';
import { TagForm } from './TagForm';
import {
  changeDeleteSuccessAlertVisibility,
  changeSubmitSuccessAlertVisibility,
} from '../../app/actions';
import { useHistoryAction } from '../../hooks';
import { TAG_FORM_TYPE } from '../constants';
import { usePublishsetTagsRetrieveResource } from '../../openapi/operations/publishsetTagsRetrieve';
import {
  publishsetTagsPartialUpdate,
  PublishsetTagsPartialUpdateRequest,
} from '../../openapi/operations/publishsetTagsPartialUpdate';
import {
  publishsetTagsCreate,
  PublishsetTagsCreateRequest,
  PublishsetTagsCreateResponse,
} from '../../openapi/operations/publishsetTagsCreate';
import { publishsetTagsDestroy } from '../../openapi/operations/publishsetTagsDestroy';
import { QUERY_STATE } from '../../constants';
import { publishsetsListOperation } from '../../openapi/operations/publishsetsList';
import { publishsetTagsListOperation } from '../../openapi/operations/publishsetTagsList';
import {
  PUBLISHSET_LIST_FILTER,
  TAGS_LIST_FILTER,
} from '../../orders/components/OrderPublishsetList';
import { actionResourceInvalidate } from '../../redux/resource/actions';

export type TagProps = {
  onCreateNewTag?(data: PublishsetTagsCreateResponse): void;
  onCloseDialog?(): void;
};

export const Tag: React.FC<TagProps> = ({ onCreateNewTag, onCloseDialog }) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id?: string }>();
  const tagId = id ? parseInt(id, 10) : null;
  const formType = tagId ? TAG_FORM_TYPE.EDIT : TAG_FORM_TYPE.NEW;
  const resource = usePublishsetTagsRetrieveResource(tagId ? { id: tagId } : null);

  const routeToTagList = useHistoryAction('vydejTagList');

  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const onSubmit = useCallback(
    async (values: PublishsetTagsCreateRequest | PublishsetTagsPartialUpdateRequest) => {
      try {
        if (tagId) {
          setSubmitQueryState(QUERY_STATE.WAITING);
          const response = await publishsetTagsPartialUpdate({
            query: { id: tagId },
            request: values,
          });

          if (response.ok) {
            setSubmitQueryState(QUERY_STATE.SUCCESS);
          } else {
            setSubmitQueryState(QUERY_STATE.FAIL);
          }
        } else {
          const response = await publishsetTagsCreate({
            request: values as PublishsetTagsCreateRequest,
          });
          if (response.ok) {
            setSubmitQueryState(QUERY_STATE.SUCCESS);
            if (onCreateNewTag) {
              onCreateNewTag(response.data);
            } else {
              dispatch(changeSubmitSuccessAlertVisibility(true));
              routeToTagList();
            }
          } else {
            setSubmitQueryState(QUERY_STATE.FAIL);
          }
        }
        const publishsetsList = publishsetsListOperation(PUBLISHSET_LIST_FILTER);
        const publishsetTagsList = publishsetTagsListOperation(TAGS_LIST_FILTER);
        dispatch(actionResourceInvalidate(publishsetsList.path));
        dispatch(actionResourceInvalidate(publishsetTagsList.path));
      } catch (e) {
        setSubmitQueryState(QUERY_STATE.FAIL);
      }
    },
    [routeToTagList, dispatch, onCreateNewTag, tagId],
  );

  const onDelete = useCallback(async () => {
    try {
      setDeleteQueryState(QUERY_STATE.WAITING);
      const response = await publishsetTagsDestroy({ query: { id: tagId! } });
      if (response.ok) {
        dispatch(changeDeleteSuccessAlertVisibility(true));
        routeToTagList();
        setDeleteQueryState(QUERY_STATE.SUCCESS);
      } else {
        setDeleteQueryState(QUERY_STATE.FAIL);
      }
    } catch (e) {
      setDeleteQueryState(QUERY_STATE.FAIL);
    }
  }, [routeToTagList, dispatch, tagId]);

  return (
    <TagForm
      key={`${resource.data?.id}-${resource.fetchedAt}`}
      formType={formType}
      resource={resource}
      submitQueryState={submitQueryState}
      onSubmit={onSubmit}
      deleteQueryState={deleteQueryState}
      onDelete={onDelete}
      onCloseDialog={onCloseDialog}
    />
  );
};
